<template>
  <slot v-if="accessStatus === 'authorized'" />

  <div
    v-else
    class="flex min-h-screen items-center justify-center bg-background p-6 text-background-fg"
  >
    <BaseInnerLoading v-if="accessStatus === 'checking'" />

    <div
      v-else
      class="w-full max-w-sm space-y-3 text-center"
    >
      <h1 class="text-xl font-semibold">Access required</h1>
      <p class="text-sm text-background-fg/70">
        To access the terminal web app, click the 'Open
        Terminal App' button on the control terminals page.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IDevice } from "@/androidPlugins/IDevice"
import {
  pairTerminalWebAppDevice,
  type PairTerminalWebAppDeviceBody,
} from "@/services/terminalWebAppAccess/pairTerminalWebAppDevice"

type AccessStatus = "checking" | "authorized" | "denied"

const TERMINAL_WEB_APP_ACCESS_TOKEN_URL_PARAM =
  "terminalWebAppAccessToken"
const TERMINAL_WEB_APP_ACCESS_TOKEN_STORAGE_KEY =
  "terminal-web-app-access-token"

const requiresControlAccess =
  import.meta.env.VITE_PLATFORM === "web" &&
  ["dev", "staging"].includes(
    import.meta.env.VITE_ENVIRONMENT
  )

const accessStatus = ref<AccessStatus>(
  requiresControlAccess ? "checking" : "authorized"
)
const {
  serialNumber,
  deviceSecret,
  hydrationRequired,
  appVersion,
  hydrateDeviceState,
} = useDevice()
const { setBaseToken, clearTokens } = useAuth()
const { setTenantState } = useTenant()
const router = useRouter()
const route = useRoute()

const getTerminalWebAppAccessToken = () => {
  const currentUrl = new URL(window.location.href)
  const fragmentParams = new URLSearchParams(
    currentUrl.hash.replace(/^#/, "")
  )
  const urlToken = fragmentParams.get(
    TERMINAL_WEB_APP_ACCESS_TOKEN_URL_PARAM
  )

  return {
    token:
      urlToken ??
      localStorage.getItem(
        TERMINAL_WEB_APP_ACCESS_TOKEN_STORAGE_KEY
      ),
  }
}

const buildPairWebAppDevicePayload = async (
  token: string
): Promise<PairTerminalWebAppDeviceBody> => {
  if (hydrationRequired.value) {
    await hydrateDeviceState()
  }

  if (
    serialNumber.value === undefined ||
    deviceSecret.value === undefined ||
    appVersion.value === undefined
  ) {
    throw new Error("Terminal web app device state missing")
  }

  const {
    model,
    operatingSystem,
    osVersion,
    webViewVersion,
  } = await IDevice.getInfo()

  return {
    token,
    serialNumber: serialNumber.value,
    deviceSecret: deviceSecret.value,
    appVersion: appVersion.value,
    model,
    operatingSystem: `${operatingSystem} ${osVersion}`,
    webViewVersion,
  }
}

onMounted(async () => {
  if (!requiresControlAccess) {
    return
  }

  accessStatus.value = "checking"

  const { token } = getTerminalWebAppAccessToken()

  if (!token) {
    accessStatus.value = "denied"
    return
  }

  try {
    const pairWebAppDevicePaylod =
      await buildPairWebAppDevicePayload(token)

    const { onboardingComplete, accessToken, tenantState } =
      await pairTerminalWebAppDevice(pairWebAppDevicePaylod)

    localStorage.setItem(
      TERMINAL_WEB_APP_ACCESS_TOKEN_STORAGE_KEY,
      token
    )

    setBaseToken(accessToken)

    if (onboardingComplete) {
      setTenantState(tenantState)

      // Kind of janky, but now tenant state has refreshed, if the user initially tried to navigate to a till route,
      // we need to refresh page to ensure correct till type (simple/quick/full) is loaded.
      if (route.path.includes("till")) {
        router.replace({ name: "home" })
      } else {
        router.replace({ hash: "" })
      }
    } else {
      await router.replace({
        name: "onboarding",
      })
    }

    accessStatus.value = "authorized"
  } catch {
    clearTokens()
    localStorage.removeItem(
      TERMINAL_WEB_APP_ACCESS_TOKEN_STORAGE_KEY
    )
    accessStatus.value = "denied"
  }
})
</script>
